import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { ICalendarWorkTemplateBase } from "@miplatform/types";
import { Box } from "@mui/material";

const LINE_TOP_OFFSET_PX = 1;

const parseTimeToHours = (time: string): number => {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return hours + minutes / 60;
};

export function AutoAssignWindowIndicators({
  workTemplateBases = [],
  hourHeight,
}: Readonly<AutoAssignWindowIndicatorsProps>) {
  const lines = useMemo(() => {
    const rawLines = workTemplateBases.flatMap((template) =>
      template.autoAssignWindows.flatMap((window, windowIndex) => [
        {
          key: `${template.id}-${windowIndex}-start`,
          top: Math.round(parseTimeToHours(window.start) * hourHeight),
          color: template.color,
        },
        {
          key: `${template.id}-${windowIndex}-end`,
          top: Math.round(parseTimeToHours(window.end) * hourHeight),
          color: template.color,
        },
      ])
    );

    return resolveOverlappingLineTops(rawLines);
  }, [workTemplateBases, hourHeight]);

  if (lines.length === 0) return null;

  return (
    <Box data-testid="auto-assign-window-indicators">
      {lines.map((line) => (
        <AutoAssignWindowLine
          key={line.key}
          top={line.top}
          color={line.color}
        />
      ))}
    </Box>
  );
}

function resolveOverlappingLineTops<
  T extends { key: string; top: number; color: string }
>(lines: T[]): T[] {
  const usedTops = new Set<number>();

  return lines.map((line) => {
    let { top } = line;
    while (usedTops.has(top)) {
      top -= LINE_TOP_OFFSET_PX;
    }
    usedTops.add(top);
    return { ...line, top };
  });
}

function AutoAssignWindowLine({
  top,
  color,
}: Readonly<{ top: number; color: string }>) {
  const { classes } = useLineStyles({ top, color });

  return <Box data-testid="auto-assign-window-line" className={classes.line} />;
}

const useLineStyles = makeStyles<{ top: number; color: string }>()(
  (_, { top, color }) => ({
    line: {
      position: "absolute",
      left: 58,
      right: 0,
      top,
      height: 0,
      borderTop: `1.5px dashed ${color}`,
      pointerEvents: "none",
    },
  })
);

export interface AutoAssignWindowIndicatorsProps {
  workTemplateBases?: ICalendarWorkTemplateBase[];
  hourHeight: number;
}
